import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { toast } from "sonner";
import { format, parseISO, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay } from "date-fns";
import { 
  FileArrowDown,
  FileXls,
  FileCsv,
  CalendarBlank,
  Funnel,
  Pencil,
  Trash,
  Check,
  X,
  CaretLeft,
  CaretRight
} from "@phosphor-icons/react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";

export default function Reports() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [locations, setLocations] = useState([]);
  const [products, setProducts] = useState([]);
  const [inventoryRecords, setInventoryRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  
  // Calendar
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [datesWithRecords, setDatesWithRecords] = useState([]);
  
  // Day records modal
  const [dayRecordsOpen, setDayRecordsOpen] = useState(false);
  const [dayRecords, setDayRecords] = useState([]);
  
  // Edit modal
  const [editingRecord, setEditingRecord] = useState(null);
  const [editForm, setEditForm] = useState({ count_cs: 0, count_ea: 0, count_lb: 0, count_oz: 0 });
  
  // Filters
  const [selectedLocation, setSelectedLocation] = useState("all");
  const [recordType, setRecordType] = useState("all");
  const [exportFormat, setExportFormat] = useState("excel");
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);

  // COGS date selectors (grouped by date)
  const [cogsSubmissions, setCogsSubmissions] = useState([]);
  const [openingDate, setOpeningDate] = useState("");
  const [closingDate, setClosingDate] = useState("");

  useEffect(() => {
    fetchData();
    fetchCogsSubmissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (inventoryRecords.length > 0) {
      extractDatesWithRecords();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventoryRecords, currentMonth]);

  const fetchData = async () => {
    try {
      const [locRes, prodRes, invRes] = await Promise.all([
        axios.get(`${API}/locations`, { withCredentials: true }),
        axios.get(`${API}/products`, { withCredentials: true }),
        axios.get(`${API}/inventory`, { withCredentials: true })
      ]);
      setLocations(locRes.data);
      setProducts(prodRes.data);
      setInventoryRecords(invRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const fetchCogsSubmissions = async () => {
    try {
      // Fetch last 6 months of submissions for dropdown
      const sixMonthsAgo = new Date();
      sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
      const start = format(sixMonthsAgo, "yyyy-MM-dd");
      const end = format(new Date(), "yyyy-MM-dd");
      const res = await axios.get(`${API}/inventory/submissions?start_date=${start}&end_date=${end}`, { withCredentials: true });
      setCogsSubmissions(res.data);
    } catch {
      console.error("Failed to load submissions");
    }
  };

  // Group submissions by date for the COGS dropdowns
  const cogsDates = useMemo(() => {
    const dateMap = {};
    for (const sub of cogsSubmissions) {
      if (selectedLocation !== "all" && sub.location_id !== selectedLocation) continue;
      if (!dateMap[sub.date]) {
        dateMap[sub.date] = { date: sub.date, locations: [], totalItems: 0, totalValue: 0, productCount: 0, prepCount: 0 };
      }
      dateMap[sub.date].locations.push(sub.location_name);
      dateMap[sub.date].totalItems += sub.total_items;
      dateMap[sub.date].totalValue += sub.total_value;
      dateMap[sub.date].productCount += sub.product_count;
      dateMap[sub.date].prepCount += sub.prep_count;
    }
    return Object.values(dateMap).sort((a, b) => b.date.localeCompare(a.date));
  }, [cogsSubmissions, selectedLocation]);

  const extractDatesWithRecords = () => {
    const dates = inventoryRecords.map(r => {
      const dateStr = r.recorded_at;
      if (typeof dateStr === 'string') {
        return dateStr.split('T')[0];
      }
      return null;
    }).filter(Boolean);
    setDatesWithRecords([...new Set(dates)]);
  };

  const getLocationName = (locationId) => {
    const loc = locations.find(l => l.location_id === locationId);
    return loc?.name || "Unknown";
  };

  const getProductName = (productId) => {
    const prod = products.find(p => p.product_id === productId);
    return prod?.name || "Unknown";
  };

  const handleDateClick = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const records = inventoryRecords.filter(r => {
      const recDate = r.recorded_at?.split('T')[0];
      return recDate === dateStr;
    });
    
    if (records.length > 0) {
      setSelectedDate(date);
      setDayRecords(records);
      setDayRecordsOpen(true);
    }
  };

  const handleEditRecord = (record) => {
    setEditingRecord(record);
    setEditForm({
      count_cs: record.count_cs || 0,
      count_ea: record.count_ea || 0,
      count_lb: record.count_lb || 0,
      count_oz: record.count_oz || 0
    });
  };

  const handleSaveEdit = async () => {
    if (!editingRecord) return;
    
    try {
      await axios.put(`${API}/inventory/${editingRecord.record_id}`, editForm, { withCredentials: true });
      toast.success("Record updated");
      setEditingRecord(null);
      fetchData();
      // Refresh day records
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const records = inventoryRecords.filter(r => r.recorded_at?.split('T')[0] === dateStr);
      setDayRecords(records);
    } catch (error) {
      toast.error("Failed to update record");
    }
  };

  const handleDeleteRecord = async (recordId) => {
    if (!window.confirm("Delete this inventory record?")) return;
    
    try {
      await axios.delete(`${API}/inventory/${recordId}`, { withCredentials: true });
      toast.success("Record deleted");
      fetchData();
      setDayRecords(prev => prev.filter(r => r.record_id !== recordId));
    } catch (error) {
      toast.error("Failed to delete record");
    }
  };

  const downloadReport = async (reportType) => {
    setDownloading(true);
    try {
      let url = `${API}/export/${reportType}?format=${exportFormat}`;
      if (selectedLocation !== "all") {
        url += `&location_id=${selectedLocation}`;
      }
      if (recordType !== "all" && reportType === "inventory") {
        url += `&record_type=${recordType}`;
      }
      
      // For COGS report, use submission-based dates if selected
      if (reportType === 'weekly-summary') {
        if (openingDate && openingDate !== "none") {
          url += `&opening_date=${openingDate}`;
        } else if (customStartDate) {
          url += `&opening_date=${format(customStartDate, 'yyyy-MM-dd')}`;
        }
        if (closingDate && closingDate !== "none") {
          url += `&closing_date=${closingDate}`;
        } else if (customEndDate) {
          url += `&closing_date=${format(customEndDate, 'yyyy-MM-dd')}`;
        }
      } else {
        // For other reports use legacy start_date/end_date
        if (customStartDate) {
          url += `&start_date=${format(customStartDate, 'yyyy-MM-dd')}`;
        }
        if (customEndDate) {
          url += `&end_date=${format(customEndDate, 'yyyy-MM-dd')}`;
        }
      }
      
      const response = await axios.get(url, { 
        withCredentials: true,
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data]);
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      
      const contentDisposition = response.headers['content-disposition'];
      let filename = `${reportType}_report.${exportFormat === 'excel' ? 'xlsx' : 'csv'}`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success(`${reportType.charAt(0).toUpperCase() + reportType.slice(1)} report downloaded!`);
    } catch (error) {
      toast.error("Failed to download report");
    } finally {
      setDownloading(false);
    }
  };

  const clearDateFilters = () => {
    setCustomStartDate(null);
    setCustomEndDate(null);
  };

  const prevMonth = () => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  // Generate calendar days
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calendarDays = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const startDayOfWeek = monthStart.getDay();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div data-testid="reports-page" className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl tracking-wide font-semibold">
          REPORTS & CALENDAR
        </h1>
        <p className="text-muted-foreground mt-1">
          View inventory history and download reports
        </p>
      </div>

      {/* Calendar Section */}
      <Card className="border rounded-xl">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading text-lg tracking-wide flex items-center gap-2">
              <CalendarBlank size={20} weight="duotone" className="text-primary" />
              INVENTORY CALENDAR
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" onClick={prevMonth}>
                <CaretLeft size={18} />
              </Button>
              <span className="font-heading text-lg tracking-wide min-w-[140px] text-center">
                {format(currentMonth, 'MMMM yyyy').toUpperCase()}
              </span>
              <Button variant="outline" size="icon" onClick={nextMonth}>
                <CaretRight size={18} />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1">
            {/* Day headers */}
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="text-center text-xs font-medium text-muted-foreground py-2">
                {day}
              </div>
            ))}
            
            {/* Empty cells for start of month */}
            {Array.from({ length: startDayOfWeek }).map((_, i) => (
              <div key={`empty-${i}`} className="h-12" />
            ))}
            
            {/* Calendar days */}
            {calendarDays.map(day => {
              const dateStr = format(day, 'yyyy-MM-dd');
              const hasRecords = datesWithRecords.includes(dateStr);
              const isToday = isSameDay(day, new Date());
              const recordCount = inventoryRecords.filter(r => r.recorded_at?.split('T')[0] === dateStr).length;
              
              return (
                <button
                  key={dateStr}
                  onClick={() => hasRecords && handleDateClick(day)}
                  className={`h-12 sm:h-16 rounded-lg flex flex-col items-center justify-center relative transition-colors ${
                    hasRecords 
                      ? 'bg-primary/10 hover:bg-primary/20 cursor-pointer' 
                      : 'hover:bg-muted/50'
                  } ${isToday ? 'ring-2 ring-primary' : ''}`}
                  disabled={!hasRecords}
                >
                  <span className={`text-sm ${isToday ? 'font-bold text-primary' : ''}`}>
                    {format(day, 'd')}
                  </span>
                  {hasRecords && (
                    <Badge className="absolute bottom-1 text-[10px] px-1.5 py-0 bg-primary">
                      {recordCount}
                    </Badge>
                  )}
                </button>
              );
            })}
          </div>
          
          <div className="mt-4 flex items-center gap-4 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-primary/10 border border-primary/30" />
              <span>Has inventory records</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded ring-2 ring-primary" />
              <span>Today</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card className="border rounded-xl">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-lg tracking-wide flex items-center gap-2">
            <Funnel size={20} weight="duotone" className="text-primary" />
            EXPORT FILTERS
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <div>
              <Label className="text-sm text-muted-foreground mb-2 block">Location</Label>
              <Select value={selectedLocation} onValueChange={setSelectedLocation}>
                <SelectTrigger data-testid="report-location-select">
                  <SelectValue placeholder="All locations" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Locations</SelectItem>
                  {locations.map(loc => (
                    <SelectItem key={loc.location_id} value={loc.location_id}>
                      {loc.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm text-muted-foreground mb-2 block">Record Type</Label>
              <Select value={recordType} onValueChange={setRecordType}>
                <SelectTrigger data-testid="report-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm text-muted-foreground mb-2 block">Start Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="report-start-date">
                    <CalendarBlank size={16} className="mr-2" />
                    {customStartDate ? format(customStartDate, "MMM d, yyyy") : "Pick date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={customStartDate}
                    onSelect={setCustomStartDate}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <Label className="text-sm text-muted-foreground mb-2 block">End Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="report-end-date">
                    <CalendarBlank size={16} className="mr-2" />
                    {customEndDate ? format(customEndDate, "MMM d, yyyy") : "Pick date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={customEndDate}
                    onSelect={setCustomEndDate}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <Label className="text-sm text-muted-foreground mb-2 block">Export Format</Label>
              <Select value={exportFormat} onValueChange={setExportFormat}>
                <SelectTrigger data-testid="export-format-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="excel">Excel (.xlsx)</SelectItem>
                  <SelectItem value="csv">CSV (.csv)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {(customStartDate || customEndDate) && (
            <div className="mt-3 flex items-center justify-between">
              <span className="text-sm text-muted-foreground">
                Date filter: {customStartDate ? format(customStartDate, "MMM d, yyyy") : "Start"} - {customEndDate ? format(customEndDate, "MMM d, yyyy") : "End"}
              </span>
              <Button variant="ghost" size="sm" onClick={clearDateFilters}>
                Clear Dates
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Report Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6">
        {/* Inventory Report */}
        <Card className="border rounded-xl hover:border-primary/50 transition-colors">
          <CardContent className="p-6">
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                {exportFormat === "excel" ? (
                  <FileXls size={32} weight="duotone" className="text-primary" />
                ) : (
                  <FileCsv size={32} weight="duotone" className="text-primary" />
                )}
              </div>
              <h3 className="font-heading text-lg tracking-wide mb-2">INVENTORY REPORT</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Products & prep items with counts, unit prices, and total values
              </p>
              <Button 
                onClick={() => downloadReport('inventory')}
                disabled={downloading}
                className="w-full bg-primary hover:bg-primary/90"
                data-testid="download-inventory-btn"
              >
                <FileArrowDown size={18} className="mr-2" />
                {downloading ? "Downloading..." : "Download"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Invoice Report */}
        <Card className="border rounded-xl hover:border-primary/50 transition-colors">
          <CardContent className="p-6">
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center mb-4">
                {exportFormat === "excel" ? (
                  <FileXls size={32} weight="duotone" className="text-accent" />
                ) : (
                  <FileCsv size={32} weight="duotone" className="text-accent" />
                )}
              </div>
              <h3 className="font-heading text-lg tracking-wide mb-2">INVOICE REPORT</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Vendor invoices with amounts and refunds
              </p>
              <Button 
                onClick={() => downloadReport('invoices')}
                disabled={downloading}
                className="w-full"
                variant="outline"
                data-testid="download-invoices-btn"
              >
                <FileArrowDown size={18} className="mr-2" />
                {downloading ? "Downloading..." : "Download"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* COGS Summary Report */}
        <Card className="border rounded-xl hover:border-primary/50 transition-colors col-span-1 sm:col-span-2 lg:col-span-3">
          <CardContent className="p-6">
            <div className="flex flex-col">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-secondary/10 flex items-center justify-center">
                  {exportFormat === "excel" ? (
                    <FileXls size={28} weight="duotone" className="text-secondary" />
                  ) : (
                    <FileCsv size={28} weight="duotone" className="text-secondary" />
                  )}
                </div>
                <div>
                  <h3 className="font-heading text-lg tracking-wide">COGS REPORT</h3>
                  <p className="text-xs text-muted-foreground">
                    Select verified counts as opening/closing anchors. Activity (invoices, waste, transfers) runs from day after opening through closing.
                  </p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                {/* Opening Count Date */}
                <div>
                  <Label className="text-xs text-muted-foreground mb-1 block">Opening Inventory Date</Label>
                  <Select value={openingDate} onValueChange={setOpeningDate}>
                    <SelectTrigger data-testid="cogs-opening-select" className="text-sm">
                      <SelectValue placeholder="Select opening count date..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-- Use date filter above --</SelectItem>
                      {cogsDates.map(d => (
                        <SelectItem key={d.date} value={d.date}>
                          {d.date} · {d.totalItems} items ({d.productCount} prod, {d.prepCount} prep)
                          {d.totalValue > 0 && <> · ${d.totalValue.toFixed(0)}</>}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {openingDate && openingDate !== "none" && (() => {
                    const d = cogsDates.find(x => x.date === openingDate);
                    return d ? (
                      <p className="text-[10px] text-muted-foreground mt-1">
                        {d.locations.join(', ')} · {d.productCount} products, {d.prepCount} prep items
                      </p>
                    ) : null;
                  })()}
                </div>
                
                {/* Closing Count Date */}
                <div>
                  <Label className="text-xs text-muted-foreground mb-1 block">Closing Inventory Date</Label>
                  <Select value={closingDate} onValueChange={setClosingDate}>
                    <SelectTrigger data-testid="cogs-closing-select" className="text-sm">
                      <SelectValue placeholder="Select closing count date..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-- Use date filter above --</SelectItem>
                      {cogsDates.map(d => (
                        <SelectItem key={d.date} value={d.date}>
                          {d.date} · {d.totalItems} items ({d.productCount} prod, {d.prepCount} prep)
                          {d.totalValue > 0 && <> · ${d.totalValue.toFixed(0)}</>}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {closingDate && closingDate !== "none" && (() => {
                    const d = cogsDates.find(x => x.date === closingDate);
                    return d ? (
                      <p className="text-[10px] text-muted-foreground mt-1">
                        {d.locations.join(', ')} · {d.productCount} products, {d.prepCount} prep items
                      </p>
                    ) : null;
                  })()}
                </div>
              </div>
              
              {/* Period Summary */}
              {openingDate && openingDate !== "none" && closingDate && closingDate !== "none" && (
                <div className="mb-4 p-3 bg-secondary/5 rounded-lg border border-secondary/20 text-sm">
                  <p className="font-medium">Period: {openingDate} to {closingDate}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Opening value from {openingDate} count &middot; Activity from {openingDate} + 1 day through {closingDate}
                  </p>
                </div>
              )}
              
              <Button 
                onClick={() => downloadReport('weekly-summary')}
                disabled={downloading}
                className="w-full sm:w-auto"
                variant="outline"
                data-testid="download-weekly-btn"
              >
                <FileArrowDown size={18} className="mr-2" />
                {downloading ? "Downloading..." : "Download COGS Report"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Waste Report */}
        <Card className="border rounded-xl hover:border-red-500/50 transition-colors">
          <CardContent className="p-6">
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mb-4">
                {exportFormat === "excel" ? (
                  <FileXls size={32} weight="duotone" className="text-red-500" />
                ) : (
                  <FileCsv size={32} weight="duotone" className="text-red-500" />
                )}
              </div>
              <h3 className="font-heading text-lg tracking-wide mb-2">WASTE REPORT</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Waste logs with items, quantities and values
              </p>
              <Button 
                onClick={() => downloadReport('waste')}
                disabled={downloading}
                className="w-full bg-red-600 hover:bg-red-700 text-white"
                data-testid="download-waste-btn"
              >
                <FileArrowDown size={18} className="mr-2" />
                {downloading ? "Downloading..." : "Download"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Day Records Modal */}
      <Dialog open={dayRecordsOpen} onOpenChange={setDayRecordsOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-wide">
              INVENTORY RECORDS - {selectedDate && format(selectedDate, 'MMMM d, yyyy').toUpperCase()}
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            {dayRecords.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="font-heading">PRODUCT</TableHead>
                    <TableHead className="font-heading">LOCATION</TableHead>
                    <TableHead className="font-heading text-right">CS</TableHead>
                    <TableHead className="font-heading text-right">EA</TableHead>
                    <TableHead className="font-heading text-right">LB</TableHead>
                    <TableHead className="font-heading text-right">OZ</TableHead>
                    <TableHead className="font-heading text-right">TOTAL</TableHead>
                    {isAdmin && <TableHead></TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {dayRecords.map(record => (
                    <TableRow key={record.record_id}>
                      <TableCell className="font-medium">{getProductName(record.product_id)}</TableCell>
                      <TableCell>{getLocationName(record.location_id)}</TableCell>
                      <TableCell className="text-right tabular-nums">{record.count_cs || 0}</TableCell>
                      <TableCell className="text-right tabular-nums">{record.count_ea || 0}</TableCell>
                      <TableCell className="text-right tabular-nums">{record.count_lb || 0}</TableCell>
                      <TableCell className="text-right tabular-nums">{record.count_oz || 0}</TableCell>
                      <TableCell className="text-right tabular-nums font-semibold">{record.quantity?.toFixed(2)}</TableCell>
                      {isAdmin && (
                        <TableCell>
                          <div className="flex gap-1">
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-8 w-8"
                              onClick={() => handleEditRecord(record)}
                            >
                              <Pencil size={14} />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-8 w-8"
                              onClick={() => handleDeleteRecord(record.record_id)}
                            >
                              <Trash size={14} className="text-destructive" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <p className="text-center text-muted-foreground py-8">No records for this date</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Record Modal */}
      <Dialog open={!!editingRecord} onOpenChange={(open) => !open && setEditingRecord(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading tracking-wide">EDIT INVENTORY RECORD</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <p className="font-medium">{editingRecord && getProductName(editingRecord.product_id)}</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>CS (Case)</Label>
                <Input 
                  type="number"
                  value={editForm.count_cs}
                  onChange={(e) => setEditForm(p => ({ ...p, count_cs: parseFloat(e.target.value) || 0 }))}
                  min="0"
                  step="0.5"
                />
              </div>
              <div>
                <Label>EA (Each)</Label>
                <Input 
                  type="number"
                  value={editForm.count_ea}
                  onChange={(e) => setEditForm(p => ({ ...p, count_ea: parseFloat(e.target.value) || 0 }))}
                  min="0"
                />
              </div>
              <div>
                <Label>LB (Pounds)</Label>
                <Input 
                  type="number"
                  value={editForm.count_lb}
                  onChange={(e) => setEditForm(p => ({ ...p, count_lb: parseFloat(e.target.value) || 0 }))}
                  min="0"
                  step="0.1"
                />
              </div>
              <div>
                <Label>OZ (Ounces)</Label>
                <Input 
                  type="number"
                  value={editForm.count_oz}
                  onChange={(e) => setEditForm(p => ({ ...p, count_oz: parseFloat(e.target.value) || 0 }))}
                  min="0"
                />
              </div>
            </div>
          </div>
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setEditingRecord(null)}>Cancel</Button>
            <Button onClick={handleSaveEdit} className="bg-primary">
              <Check size={16} className="mr-2" />
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
